import React from 'react';
import intl from 'react-intl-universal';

import {useDsManager, useRootStore} from '@stores/AppStateContext';
import {useBasicDataSourceDeps} from '../../../platform/hooks/useDataSourceDependencies';
import type {AuditTemplate} from '../../../stores/audit/types';
import {unpublishAudit} from '../../../actions/audit/unpublishAudit';
import {ModalConfirm} from '../../components/ModalConfirm/ModalConfirm';

export interface AuditUnpublishConfirmModalProps {
    template: AuditTemplate | null;
    onClose: () => void;
}

export const AuditUnpublishConfirmModal = ({template, onClose}: AuditUnpublishConfirmModalProps) => {
    const rootStore = useRootStore();
    const dsManager = useDsManager();
    const dsDeps = useBasicDataSourceDeps();
    const wsId = rootStore.view.workspace?.id;
    const auditName = template?.name || intl.get('audits.list.untitled').d('Untitled');

    const handleUnpublish = async () => {
        if (!template || !wsId) return;
        const updated = await unpublishAudit(dsDeps.dataClientService, dsDeps.authService, wsId, template.id);
        dsManager?.getAuditsDataSource(wsId).upsertAuditTemplate(updated);
        onClose();
    };

    return (
        <ModalConfirm
            open={!!template}
            type={'warning'}
            header={intl.get('audits.unpublish_confirm.header').d('Unpublish audit')}
            confirmText={intl.get('audits.unpublish_confirm.confirm-btn').d('Unpublish')}
            onConfirm={handleUnpublish}
            onCancel={onClose}
            onClose={onClose}
        >
            <div>
                {intl.getHTML('audits.unpublish_confirm.text', {auditName})
                    .d(`<b>Unpublish “{auditName}”?</b> It goes back to draft — hidden from the Overview table and the audit picker, so it can't be assigned to new locations.`)}
            </div>
            <br/>
            {intl.get('audits.unpublish_confirm.text_addition').d("Locations that already have this audit assigned or in progress keep it and can still complete it — unpublishing doesn't cancel work already handed out.")}
        </ModalConfirm>
    );
};
